import { useState, useEffect, useCallback } from 'react'

import ErrorDisplay from '../../../components/error/ErrorDisplay'
import { Typography, Button, Box } from '@mui/material'
import { getHeaders } from '../../../utils/common'
import { DialogWrapper } from '../../../components/general/DialogWrapper'
import Loading from '../../../Loading'

/**
 * @typedef {Interface} DeleteOrganisationParams
 * @prop {Object} organisation
 * @prop {String} organisation.id id of the organisation
 * @prop {String} organisation.name name of the organisation
 * @prop {boolean} isOpen
 * @prop {Function} onDelete
 * @prop {Function} closeModal
 */
interface DeleteOrganisationParams {
  organisation: {
    id: string
    name: string
  } | null
  isOpen: boolean
  onDelete: () => void
  closeModal: () => void
}

/**
 * Delete organisation modal
 * @param {DeleteOrganisationParams}
 * @returns {JSX.Element}
 */
export default function DeleteOrganisation({ organisation, onDelete, isOpen, closeModal }: DeleteOrganisationParams) {
  const [deleteError, setDeleteError] = useState(null)
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false)
  const myHeaders = getHeaders()
  myHeaders.append('Content-Type', 'application/json')
  useEffect(() => {
    setDeleteError(null)
    setIsDeleteInProgress(false)
  }, [isOpen])

  const submitDelete = useCallback(() => {
    setDeleteError(null)
    setIsDeleteInProgress(true)

    fetch(`/mqttapi/v1/organisation/${organisation?.id}`, {
      method: 'DELETE',
      headers: myHeaders,
    })
      .then(async (response) => {
        if (response.ok) {
          return response.text()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then(() => {
        onDelete()
        closeModal()
      })
      .catch((error) => setDeleteError(error))
      .finally(() => setIsDeleteInProgress(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation, onDelete, setIsDeleteInProgress, closeModal])

  return (
    <DialogWrapper isOpen={isOpen} closeModal={closeModal}>
      <strong>Are you sure you want to delete {organisation?.name || ''}?</strong>
      <Typography variant="body1">
        This will also delete all sessions belonging to the organisation
        <br />
        and make any remaining users sad (probably).
      </Typography>
      {isDeleteInProgress ? (
        <Loading action="Deleting..." />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'right', gap: '20px' }}>
          <Button variant="contained" color="error" onClick={submitDelete}>
            Yes, DO IT
          </Button>
          <Button variant="contained" color="primary" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
      )}
      {deleteError !== null ? <ErrorDisplay err={deleteError} /> : null}
    </DialogWrapper>
  )
}
